const AUTH_STORAGE_KEY = 'solid-sales-auth';
const OIDC_AUTH_PORT = 8080;

function getAuthAppUrl(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:${OIDC_AUTH_PORT}`;
  }
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return `http://${hostname}:${OIDC_AUTH_PORT}`;
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    parts[0] = 'auth';
    return `https://${parts.join('.')}`;
  }
  return `http://localhost:${OIDC_AUTH_PORT}`;
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const hash = await sha256(codeVerifier);
  return base64UrlEncode(hash);
}

export interface AuthCallbackResult {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  error?: string;
}

export async function handleAuthCallback(): Promise<AuthCallbackResult> {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const error = urlParams.get('error');
  const errorDesc = urlParams.get('error_description');

  if (error) {
    return { success: false, error: errorDesc || error };
  }

  if (code) {
    const stateParam = urlParams.get('state');
    let codeVerifier: string | null = null;
    let redirectUrl = '/admin';

    if (stateParam) {
      try {
        const decoded = atob(stateParam.replace(/-/g, '+').replace(/_/g, '/'));
        const stateData = JSON.parse(decoded);
        codeVerifier = stateData.cv || null;
        redirectUrl = stateData.redirect || '/admin';
      } catch {
        redirectUrl = stateParam;
        codeVerifier = sessionStorage.getItem('oidc_code_verifier');
      }
    } else {
      codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    }

    if (!codeVerifier) {
      return { success: false, error: 'Missing PKCE code verifier' };
    }

    try {
      const tokenResponse = await fetch('/oauth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: 'solid-sales',
          code,
          code_verifier: codeVerifier,
          redirect_uri: `${window.location.origin}/auth/callback`,
        }).toString(),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        return { success: false, error: errorData.error_description || errorData.error || 'Token exchange failed' };
      }

      const tokenData = await tokenResponse.json();

      if (tokenData.error) {
        return { success: false, error: tokenData.error_description || tokenData.error };
      }

      const { access_token: accessToken, refresh_token: refreshToken, id_token: idToken } = tokenData;

      if (!accessToken) {
        return { success: false, error: 'No access token received from auth server' };
      }

      let user = null;
      if (idToken) {
        try {
          const parts = idToken.split('.');
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          user = {
            id: payload.sub,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
            orgs: payload.orgs || [],
            apps: payload.apps || [],
          };
        } catch (e) {
          console.warn('[AuthCallback] Failed to decode ID token:', e);
        }
      }

      const authState = {
        state: { accessToken, refreshToken: refreshToken || null, user, isAuthenticated: true },
        version: 0,
      };

      // Write to Sales auth store
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authState));

      // Also write to solid-hub-auth so the app-rail can find the user/apps data
      localStorage.setItem('solid-hub-auth', JSON.stringify(authState));

      sessionStorage.removeItem('oidc_code_verifier');
      sessionStorage.removeItem('oidc_state');

      window.history.replaceState(null, '', redirectUrl);
      window.location.reload();

      return { success: true, accessToken, refreshToken };
    } catch (err) {
      console.error('[AuthCallback] Token exchange error:', err);
      return { success: false, error: 'Token exchange failed' };
    }
  }

  return { success: false };
}

export interface RefreshedTokens {
  accessToken: string;
  refreshToken: string | null;
}

/**
 * Exchange a refresh token for new tokens. The auth server rotates refresh
 * tokens on every use, so the returned refreshToken MUST replace the stored
 * one — reusing the old token trips reuse detection and revokes the grant.
 */
export async function refreshAccessToken(refreshToken: string): Promise<RefreshedTokens | null> {
  try {
    const response = await fetch('/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: 'solid-sales',
        refresh_token: refreshToken,
      }).toString(),
    });

    if (!response.ok) return null;
    const data = await response.json();
    if (data.error || !data.access_token) return null;
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || null,
    };
  } catch {
    return null;
  }
}

export async function getAuthLoginUrl(redirectUrl?: string): Promise<string> {
  const authBaseUrl = getAuthAppUrl();
  const redirect = redirectUrl || window.location.origin;

  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const stateData = JSON.stringify({ redirect, cv: codeVerifier });
  const stateEncoded = btoa(stateData).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  sessionStorage.setItem('oidc_code_verifier', codeVerifier);
  sessionStorage.setItem('oidc_state', redirect);

  const url = new URL(`${authBaseUrl}/oauth/authorize`);
  url.searchParams.set('client_id', 'solid-sales');
  url.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email orgs apps offline_access');
  // Auth server issues refresh tokens only when offline_access arrives as an
  // extra param — oidc-provider strips it from the scope string.
  url.searchParams.set('offline_access', 'true');
  url.searchParams.set('state', stateEncoded);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');

  return url.toString();
}
